import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SignUpForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.businessName?.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email address is invalid';
    }

    if (!formData?.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (!formData?.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await signUp(
        formData?.email,
        formData?.password,
        formData?.fullName,
        formData?.businessName,
        formData?.phoneNumber
      );

      // Show success message
      setShowSuccessMessage(true);
      
      // Clear form
      setFormData({
        fullName: '',
        businessName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: ''
      });

    } catch (error) {
      console.error('Sign up error:', error);
      setErrors({ 
        submit: error?.message || 'Failed to create account. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccessMessage) {
    return (
      <div className="text-center space-y-6">
        <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-heading text-foreground">Check your email</h3>
          <p className="text-sm text-muted-foreground">
            We have sent you a confirmation link at <strong>{formData?.email}</strong>
          </p>
        </div>
        <Button
          onClick={() => setShowSuccessMessage(false)}
          className="w-full btn-dark-outline transition-luxury"
        >
          Back to Sign Up
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Full Name Input */}
      <div className="space-y-2">
        <label htmlFor="fullName" className="text-sm font-medium text-foreground">
          Full Name *
        </label>
        <Input
          id="fullName"
          name="fullName"
          type="text"
          value={formData?.fullName}
          onChange={handleInputChange}
          className={`input-dark ${errors?.fullName ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
          placeholder="Enter your full name"
        />
        {errors?.fullName && (
          <p className="text-xs text-destructive">{errors?.fullName}</p>
        )}
      </div>

      {/* Business Name Input */}
      <div className="space-y-2">
        <label htmlFor="businessName" className="text-sm font-medium text-foreground">
          Business Name *
        </label>
        <Input
          id="businessName"
          name="businessName"
          type="text"
          value={formData?.businessName}
          onChange={handleInputChange}
          className={`input-dark ${errors?.businessName ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
          placeholder="Enter your business name"
        />
        {errors?.businessName && (
          <p className="text-xs text-destructive">{errors?.businessName}</p>
        )}
      </div>

      {/* Email Input */}
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-foreground">
          Email Address *
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData?.email}
          onChange={handleInputChange}
          className={`input-dark ${errors?.email ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
          placeholder="Enter your email address"
        />
        {errors?.email && (
          <p className="text-xs text-destructive">{errors?.email}</p>
        )}
      </div>

      {/* Phone Number Input */}
      <div className="space-y-2">
        <label htmlFor="phoneNumber" className="text-sm font-medium text-foreground">
          Phone Number *
        </label>
        <Input
          id="phoneNumber"
          name="phoneNumber"
          type="tel"
          value={formData?.phoneNumber}
          onChange={handleInputChange}
          className={`input-dark ${errors?.phoneNumber ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
          placeholder="Enter your phone number"
        />
        {errors?.phoneNumber && (
          <p className="text-xs text-destructive">{errors?.phoneNumber}</p>
        )}
      </div>

      {/* Password Input */}
      <div className="space-y-2">
        <label htmlFor="password" className="text-sm font-medium text-foreground">
          Password *
        </label>
        <Input
          id="password"
          name="password"
          type="password"
          value={formData?.password}
          onChange={handleInputChange}
          className={`input-dark ${errors?.password ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
          placeholder="Create a password"
        />
        {errors?.password && (
          <p className="text-xs text-destructive">{errors?.password}</p>
        )}
      </div>

      {/* Confirm Password Input */}
      <div className="space-y-2">
        <label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
          Re-Enter Password *
        </label>
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          value={formData?.confirmPassword}
          onChange={handleInputChange}
          className={`input-dark ${errors?.confirmPassword ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
          placeholder="Confirm your password"
        />
        {errors?.confirmPassword && (
          <p className="text-xs text-destructive">{errors?.confirmPassword}</p>
        )}
      </div>

      {/* Two Factor Authentication Notice */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 text-primary flex-shrink-0 mt-0.5">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
            <p className="text-xs text-muted-foreground">
              We'll send you an email verification link to secure your account.
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full btn-teal transition-luxury-slow"
        disabled={isLoading}
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>

      {/* Error Display */}
      {errors?.submit && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
          <p className="text-sm text-destructive">{errors?.submit}</p>
        </div>
      )}

      {/* Social Sign Up Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">or</span>
        </div>
      </div>

      {/* Social Sign Up Buttons */}
      <div className="space-y-3">
        <Button
          type="button"
          className="w-full btn-dark-outline transition-luxury"
          onClick={(e) => e?.preventDefault()}
        >
          <svg className="mr-3 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </Button>
        
        <Button
          type="button"
          className="w-full btn-dark-outline transition-luxury"
          onClick={(e) => e?.preventDefault()}
        >
          <svg className="mr-3 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Sign up with Facebook
        </Button>
      </div>

      {/* Terms and Privacy Agreement */}
      <div className="text-center text-xs text-muted-foreground leading-relaxed">
        By signing up, you agree to our{' '}
        <a href="#" className="text-primary hover:text-primary/80 transition-luxury">
          Terms of Service
        </a>
        {' '}and{' '}
        <a href="#" className="text-primary hover:text-primary/80 transition-luxury">
          Data Processing Agreement
        </a>
        .
      </div>

      {/* Sign In Link */}
      <div className="text-center text-sm">
        <span className="text-muted-foreground">Already have an account? </span>
        <Link
          to="/login"
          className="text-primary hover:text-primary/80 font-medium transition-luxury"
        >
          Sign In
        </Link>
      </div>
    </form>
  );
};

export default SignUpForm;